import { useEffect, useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/stores/useAuthStore';
import { useBabyStore, useActiveBaby } from '@/stores/useBabyStore';
import { useBabyAge } from '@/hooks/useBabyAge';
import { api, authHeaders, type ProfileResponse } from '@/lib/api-client';
import type { JourneyResponse } from '@/types/journey';
import { BabyAgeChip } from '@/components/profile/BabyAgeChip';
import { BabyProfileForm } from '@/components/profile/BabyProfileForm';
import { CountryEdit } from '@/components/profile/CountryEdit';
import { PushNotificationsToggle } from '@/components/profile/PushNotificationsToggle';
import { DIET_OPTIONS } from '@/components/profile/diet-options';
import { BottomNav } from '@/components/nav/BottomNav';
import { FullPageState } from '@/components/ui/PageState';
import { Icon, IconTile } from '@/components/ui/icons';
import type { Profile, DietPreference, BirthMode, FitnessLevel } from '@/types';
import {
  isGerman,
  getPriceDisplay,
  getLanguageSetting,
  setLanguageSetting,
  type LanguageSetting,
} from '@/i18n/locale';

export default function Profile() {
  const { babies, loaded, load } = useBabyStore();
  const updateBaby = useBabyStore((s) => s.update);
  const baby = useActiveBaby();
  const signOut = useAuthStore((s) => s.signOut);
  const resetBabies = useBabyStore((s) => s.reset);
  const navigate = useNavigate();

  const [journey, setJourney] = useState<JourneyResponse | null>(null);
  const [profileResponse, setProfileResponse] = useState<ProfileResponse | null>(null);

  const de = isGerman();

  useEffect(() => {
    if (!loaded) load();
  }, [loaded, load]);

  useEffect(() => {
    api.profile
      .get()
      .then(setProfileResponse)
      .catch(() => {
        /* swallow — profile-dependent cards skip render if null */
      });
  }, []);

  useEffect(() => {
    if (!baby) return;
    api.journey
      .thisWeek(baby.id)
      .then(setJourney)
      .catch(() => {
        /* swallow — journey is best-effort here, errors don't break the page */
      });
  }, [baby?.id]);

  if (!loaded) {
    return <FullPageState />;
  }

  if (babies.length === 0) {
    return <Navigate to="/onboarding" replace />;
  }

  if (!baby) {
    return <FullPageState busy={false} label={de ? 'Kein Baby ausgewählt.' : 'No baby selected.'} />;
  }

  const profileFields = profileResponse?.profile ?? null;
  const usage = profileResponse
    ? {
        tier: (profileResponse.profile.subscription_tier as 'free' | 'pro') ?? 'free',
        used: profileResponse.usage.used,
        limit: profileResponse.usage.limit,
        token_balance: profileResponse.usage.token_balance,
      }
    : null;

  return (
    <div className="h-[100dvh] bg-cream flex flex-col">
      <header className="border-b border-primary-50 bg-cream/95 backdrop-blur sticky top-0 z-10">
        <div className="mx-auto max-w-chat px-4 py-3 flex items-center justify-between gap-3">
          <h1 className="font-display text-lg font-semibold text-slate">
            {de ? 'Profil' : 'Profile'}
          </h1>
          <BabyAgeChip baby={baby} />
        </div>
      </header>

      <main className="flex-1 overflow-y-auto mx-auto max-w-chat w-full px-4 py-6 space-y-6">
        <BabyHeaderCard baby={baby} email={profileFields?.email ?? null} />

        {usage && <SubscriptionCard usage={usage} />}

        <section className="card p-5">
          <BabyProfileForm
            title={de ? `${baby.name}s Profil` : `${baby.name}'s profile`}
            initial={baby}
            onSubmit={async (values) => {
              await updateBaby(baby.id, values);
            }}
          />
        </section>

        {profileFields && (
          <MotherProfileSection
            initial={{
              diet_preferences: profileFields.diet_preferences ?? [],
              birth_mode: profileFields.birth_mode ?? null,
              fitness_level: profileFields.fitness_level ?? null,
            }}
            onSaved={(updated) =>
              setProfileResponse((p) => (p ? { ...p, profile: updated } : p))
            }
          />
        )}

        {profileFields && (
          <CountryEdit
            initial={profileFields.country ?? null}
            onSaved={(updated) =>
              setProfileResponse((p) => (p ? { ...p, profile: updated } : p))
            }
          />
        )}

        <PushNotificationsToggle />

        {journey && <JourneyProgressCard journey={journey} />}

        <LanguageSection />

        <LegalSection />

        <div className="pt-2">
          <button
            type="button"
            onClick={async () => {
              await signOut();
              resetBabies();
              navigate('/', { replace: true });
            }}
            className="btn-ghost w-full text-slate-muted hover:text-slate"
          >
            {de ? 'Abmelden' : 'Sign out'}
          </button>
        </div>
      </main>

      <BottomNav />
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// Header card — avatar + name + age, photo upload placeholder
// ════════════════════════════════════════════════════════════════════

function BabyHeaderCard({
  baby,
  email,
}: {
  baby: { id: string; name: string; date_of_birth: string };
  email: string | null;
}) {
  const ageLabel = useBabyAge(baby.date_of_birth);
  const initial = baby.name.trim().charAt(0).toUpperCase() || '·';
  const de = isGerman();

  return (
    <section className="card p-5">
      <div className="flex items-center gap-4">
        <div
          className="shrink-0 w-16 h-16 rounded-full bg-primary text-white flex items-center justify-center font-display text-2xl font-semibold"
          aria-hidden
        >
          {initial}
        </div>
        <div className="min-w-0 flex-1">
          <h2 className="font-display text-xl font-semibold text-slate truncate">
            {baby.name}
          </h2>
          <div className="text-sm text-slate-muted mt-0.5">{ageLabel}</div>
        </div>
      </div>
      <div className="mt-4 pt-4 border-t border-primary-50 space-y-1.5 text-xs text-slate-muted">
        <p className="flex items-start gap-1.5">
          <Icon.Camera size={14} className="shrink-0 mt-0.5" />
          <span>
            {de
              ? 'Foto-Upload kommt bald. Bis dahin: ein freundlicher Anfangsbuchstabe.'
              : "Photo upload coming soon. For now, that's a friendly initial."}
          </span>
        </p>
        {email && (
          <p className="truncate">
            {de ? 'Angemeldet als ' : 'Signed in as '}
            <span className="text-slate">{email}</span>
          </p>
        )}
      </div>
    </section>
  );
}

// ════════════════════════════════════════════════════════════════════
// Subscription card — visible upgrade entry point. Free users see a
// progress bar + "Upgrade" CTA. Pro users see a quieter confirmation
// plus the Stripe Customer Portal link (FTC click-to-cancel path).
// ════════════════════════════════════════════════════════════════════

interface UsageState {
  tier: 'free' | 'pro';
  used: number;
  limit: number | null;
  token_balance: number;
}

function SubscriptionCard({ usage }: { usage: UsageState }) {
  if (usage.tier === 'pro') {
    return <ProSubscriptionCard />;
  }
  return <FreeSubscriptionCard usage={usage} />;
}

function ProSubscriptionCard() {
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const de = isGerman();

  const openPortal = async () => {
    setLoading(true);
    setErr(null);
    try {
      const res = await fetch('/api/billing/portal', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(await authHeaders()) },
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error ?? `HTTP ${res.status}`);
      }
      const json = (await res.json()) as { url?: string };
      if (!json.url) throw new Error('No portal URL returned');
      window.location.assign(json.url);
    } catch (e) {
      console.error('[profile] portal failed:', e);
      setErr(
        de
          ? 'Der Abo-Manager konnte nicht geöffnet werden. Bitte gleich nochmal probieren.'
          : "Couldn't open the subscription manager. Try again in a moment."
      );
      setLoading(false);
    }
  };

  return (
    <section className="rounded-2xl bg-slate text-white p-5 shadow-sm">
      <div className="flex items-start gap-3">
        <div className="shrink-0 w-10 h-10 rounded-xl bg-accent/20 text-accent-300 flex items-center justify-center" aria-hidden>
          <Icon.Sparkles size={20} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h2 className="font-display font-semibold text-white">Alma Pro</h2>
            <span className="text-[10px] uppercase tracking-wider font-semibold bg-accent/20 text-accent-300 px-2 py-0.5 rounded-full">
              {de ? 'Aktiv' : 'Active'}
            </span>
          </div>
          <p className="text-sm text-white/70 mt-1.5 leading-relaxed">
            {de
              ? 'Unbegrenzte Fragen, mehrere Babys, vollständiger Verlauf. Danke, dass du Alma unterstützt.'
              : 'Unlimited questions, multiple babies, full history. Thanks for supporting Alma.'}
          </p>

          <div className="mt-4">
            <button
              type="button"
              onClick={openPortal}
              disabled={loading}
              className="inline-flex items-center justify-center rounded-lg bg-white/10 hover:bg-white/15 transition-colors text-sm text-white px-3.5 py-2 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {loading ? (de ? 'Öffnet…' : 'Opening…') : de ? 'Abo verwalten' : 'Manage subscription'}
            </button>
            <span className="ml-3 text-xs text-white/60">
              {de
                ? 'Zahlung ändern, Tarif wechseln oder kündigen.'
                : 'Update payment, switch plans, or cancel.'}
            </span>
          </div>

          {err && <div className="mt-3 text-xs text-accent-300">{err}</div>}
        </div>
      </div>
    </section>
  );
}

function FreeSubscriptionCard({ usage }: { usage: UsageState }) {
  const limit = usage.limit ?? 20;
  const pct = Math.min(100, Math.round((usage.used / limit) * 100));
  const nearLimit = pct >= 75;
  const de = isGerman();
  const price = getPriceDisplay();

  return (
    <section className="rounded-2xl border border-accent/40 bg-gradient-to-br from-accent/15 to-accent/5 p-5">
      <div className="flex items-start gap-3">
        <IconTile tone="accent">
          <Icon.Sparkles size={20} />
        </IconTile>
        <div className="flex-1 min-w-0">
          <h2 className="font-display font-semibold text-slate">
            {de ? 'Kostenlos-Tarif' : 'Free plan'}
          </h2>
          <p className="text-sm text-slate-muted mt-1 leading-relaxed">
            {de
              ? `${usage.used} von ${limit} Fragen diesen Monat genutzt`
              : `${usage.used} of ${limit} questions used this month`}
            {usage.token_balance > 0 &&
              (de ? ` · ${usage.token_balance} Bonus-Credits` : ` · ${usage.token_balance} bonus credits`)}
          </p>

          <div
            className="mt-3 h-1.5 rounded-full bg-white/60 overflow-hidden"
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={limit}
            aria-valuenow={Math.min(usage.used, limit)}
            aria-label={de ? 'Genutzte Fragen diesen Monat' : 'Questions used this month'}
          >
            <div
              className={
                'h-full rounded-full transition-[width] ' + (nearLimit ? 'bg-accent-600' : 'bg-primary')
              }
              style={{ width: `${pct}%` }}
            />
          </div>

          <div className="mt-4 flex flex-wrap gap-2 items-center">
            <Link to="/pricing" className="btn-primary text-sm py-2 px-4 inline-flex items-center">
              {de ? 'Upgrade auf Pro →' : 'Upgrade to Pro →'}
            </Link>
            <span className="text-xs text-slate-muted">
              {de
                ? `${price.monthly}/Monat · unbegrenzte Fragen`
                : `${price.monthly}/mo · unlimited questions`}
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}

// ════════════════════════════════════════════════════════════════════
// MotherProfileSection — "About you" card. Moved from Settings on
// 2026-05-31 when Settings collapsed into Profile. Three optional
// fields that feed nutrition + daily-content personalization. See
// migration 009 and docs/features/mother-profile.md.
// ════════════════════════════════════════════════════════════════════

function MotherProfileSection({
  initial,
  onSaved,
}: {
  initial: {
    diet_preferences: DietPreference[];
    birth_mode: BirthMode | null;
    fitness_level: FitnessLevel | null;
  };
  onSaved: (profile: Profile) => void;
}) {
  const de = isGerman();
  const [diet, setDiet] = useState<DietPreference[]>(initial.diet_preferences);
  const [birthMode, setBirthMode] = useState<BirthMode | null>(initial.birth_mode);
  const [fitness, setFitness] = useState<FitnessLevel | null>(initial.fitness_level);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [savedFlash, setSavedFlash] = useState(false);

  const toggleDiet = (v: DietPreference) => {
    setDiet((cur) => (cur.includes(v) ? cur.filter((d) => d !== v) : [...cur, v]));
  };

  const onSave = async () => {
    setSaving(true);
    setErr(null);
    try {
      const { profile } = await api.profile.updateMother({
        diet_preferences: diet,
        birth_mode: birthMode,
        fitness_level: fitness,
      });
      onSaved(profile);
      setSavedFlash(true);
      window.setTimeout(() => setSavedFlash(false), 1800);
    } catch (e) {
      setErr((e as Error).message ?? (de ? 'Speichern fehlgeschlagen.' : 'Failed to save.'));
    } finally {
      setSaving(false);
    }
  };

  const birthOptions: Array<{ v: BirthMode | null; en: string; de: string }> = [
    { v: 'vaginal',   en: 'Vaginal',           de: 'Vaginal' },
    { v: 'c_section', en: 'C-section',         de: 'Kaiserschnitt' },
    { v: null,        en: 'Prefer not to say', de: 'Keine Angabe' },
  ];

  const fitnessOptions: Array<{ v: FitnessLevel | null; en: string; de: string }> = [
    { v: 'beginner',  en: 'Beginner',          de: 'Anfängerin' },
    { v: 'returning', en: 'Returning',         de: 'Wiedereinstieg' },
    { v: 'active',    en: 'Active',            de: 'Aktiv' },
    { v: null,        en: 'Prefer not to say', de: 'Keine Angabe' },
  ];

  return (
    <section className="card p-5">
      <h2 className="font-display font-semibold text-slate">
        {de ? 'Über dich' : 'About you'}
      </h2>
      <p className="text-xs text-slate-muted mt-1 leading-relaxed">
        {de
          ? 'Hilft Alma, dir passende Mahlzeit- und Fitness-Ideen zu geben. Alles optional.'
          : 'Helps Alma tailor meal and fitness ideas for you. All optional.'}
      </p>

      <div className="mt-4">
        <span className="text-sm text-slate font-medium mb-1.5 block">
          {de ? 'Ernährung' : 'Diet'}{' '}
          <span className="text-slate-muted font-normal">
            ({de ? 'Mehrfachauswahl' : 'multi-select'})
          </span>
        </span>
        <div className="flex flex-wrap gap-2">
          {DIET_OPTIONS.map((opt) => {
            const on = diet.includes(opt.v);
            return (
              <button
                key={opt.v}
                type="button"
                onClick={() => toggleDiet(opt.v)}
                aria-pressed={on}
                className="choice-pill-sm"
              >
                {de ? opt.de : opt.en}
              </button>
            );
          })}
        </div>
      </div>

      <div className="mt-5">
        <span className="text-sm text-slate font-medium mb-1.5 block">
          {de ? 'Geburt' : 'Birth'}
        </span>
        <div className="grid grid-cols-3 gap-2" role="radiogroup" aria-label={de ? 'Geburt' : 'Birth'}>
          {birthOptions.map((opt) => {
            const on = birthMode === opt.v;
            return (
              <button
                key={opt.en}
                type="button"
                role="radio"
                aria-checked={on}
                onClick={() => setBirthMode(opt.v)}
                className="choice-segment"
              >
                {de ? opt.de : opt.en}
              </button>
            );
          })}
        </div>
      </div>

      <div className="mt-5">
        <span className="text-sm text-slate font-medium mb-1.5 block">
          {de ? 'Fitness' : 'Fitness level'}
        </span>
        <div className="grid grid-cols-2 gap-2" role="radiogroup" aria-label={de ? 'Fitness' : 'Fitness level'}>
          {fitnessOptions.map((opt) => {
            const on = fitness === opt.v;
            return (
              <button
                key={opt.en}
                type="button"
                role="radio"
                aria-checked={on}
                onClick={() => setFitness(opt.v)}
                className="choice-segment"
              >
                {de ? opt.de : opt.en}
              </button>
            );
          })}
        </div>
      </div>

      {err && (
        <div className="mt-4 rounded-lg bg-danger/5 border border-danger/30 px-3 py-2 text-sm text-danger-700">
          {err}
        </div>
      )}
      {savedFlash && (
        <div
          className="mt-4 flex items-center gap-1.5 rounded-lg bg-primary-50 border border-primary-100 px-3 py-2 text-sm text-primary-800"
          role="status"
        >
          <Icon.Check size={16} />
          {de ? 'Gespeichert' : 'Saved'}
        </div>
      )}

      <button
        type="button"
        onClick={onSave}
        disabled={saving}
        className="btn-primary w-full mt-5"
      >
        {saving ? (de ? 'Speichert…' : 'Saving…') : de ? 'Speichern' : 'Save changes'}
      </button>
    </section>
  );
}

// ════════════════════════════════════════════════════════════════════
// Journey progress card — derived from journey response. Cards have
// three lifecycle states: unseen | seen | completed.
// ════════════════════════════════════════════════════════════════════

function JourneyProgressCard({ journey }: { journey: JourneyResponse }) {
  const total = journey.cards.length;
  const completed = journey.cards.filter((c) => c.state === 'completed').length;
  const seen = journey.cards.filter((c) => c.state === 'seen').length;
  const remaining = total - completed - seen;

  const completedPct = total === 0 ? 0 : Math.round((completed / total) * 100);
  const seenPct = total === 0 ? 0 : Math.round((seen / total) * 100);
  const de = isGerman();

  return (
    <section className="card p-5">
      <h2 className="font-display font-semibold text-slate">
        {de ? 'Karten dieser Woche' : "This week's cards"}
      </h2>
      <p className="text-xs text-slate-muted mt-1">
        {total === 0
          ? de
            ? 'Aktuell gibt es keine geplanten Karten für dieses Alter.'
            : "We're between scheduled cards for this age."
          : de
            ? `${total} ${total === 1 ? 'Karte' : 'Karten'} für ${journey.baby.age_weeks} ${
                journey.baby.age_weeks === 1 ? 'Woche' : 'Wochen'
              }.`
            : `${total} card${total === 1 ? '' : 's'} for ${journey.baby.age_weeks} week${
                journey.baby.age_weeks === 1 ? '' : 's'
              } old.`}
      </p>

      {total > 0 && (
        <>
          <div
            className="mt-4 h-2 rounded-full bg-primary-50 overflow-hidden flex"
            role="img"
            aria-label={
              de
                ? `${completed} erledigt, ${seen} gesehen, ${remaining} offen`
                : `${completed} completed, ${seen} seen, ${remaining} to explore`
            }
          >
            <div className="h-full bg-primary transition-[width]" style={{ width: `${completedPct}%` }} />
            <div className="h-full bg-primary-300 transition-[width]" style={{ width: `${seenPct}%` }} />
          </div>
          <dl className="mt-3 grid grid-cols-3 gap-2 text-center text-xs">
            <Stat label={de ? 'Erledigt' : 'Completed'} value={completed} accent />
            <Stat label={de ? 'Gesehen' : 'Seen'} value={seen} />
            <Stat label={de ? 'Noch offen' : 'To explore'} value={remaining} muted />
          </dl>
        </>
      )}
    </section>
  );
}

function Stat({
  label,
  value,
  accent,
  muted,
}: {
  label: string;
  value: number;
  accent?: boolean;
  muted?: boolean;
}) {
  return (
    <div className="flex flex-col-reverse">
      <dt className="text-[11px] text-slate-muted mt-1">{label}</dt>
      <dd
        className={
          'font-display text-xl font-semibold leading-none tabular-nums ' +
          (accent ? 'text-primary-700' : muted ? 'text-slate-muted' : 'text-slate')
        }
      >
        {value}
      </dd>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// LanguageSection — moved from Settings on 2026-05-31.
// Radio group with three values: Deutsch / English / Automatic.
// Clicking a different value persists it to localStorage and reloads
// the page so every component re-renders in the new language.
// ════════════════════════════════════════════════════════════════════

function LanguageSection() {
  const de = isGerman();
  const [current, setCurrent] = useState<LanguageSetting>(() => getLanguageSetting());

  const onSelect = (value: LanguageSetting) => {
    if (value === current) return;
    setCurrent(value);
    // setLanguageSetting() reloads the page after persisting.
    setLanguageSetting(value);
  };

  const options: Array<{ value: LanguageSetting; badge: React.ReactNode; label: string }> = [
    { value: 'de', badge: 'DE', label: 'Deutsch' },
    { value: 'en', badge: 'EN', label: 'English' },
    {
      value: 'auto',
      badge: <Icon.Globe size={16} />,
      label: de ? 'Automatisch (Browser)' : 'Automatic (browser)',
    },
  ];

  return (
    <section className="card p-5">
      <h2 className="font-display font-semibold text-slate">
        {de ? 'Sprache' : 'Language'}
      </h2>
      <p className="text-xs text-slate-muted mt-1 leading-relaxed">
        {de
          ? 'Wähle, in welcher Sprache Alma mit dir spricht. „Automatisch" folgt der Sprache deines Browsers.'
          : 'Choose which language Alma uses with you. "Automatic" follows your browser language.'}
      </p>

      <div className="mt-4 space-y-2" role="radiogroup" aria-label={de ? 'Sprache' : 'Language'}>
        {options.map((opt) => {
          const checked = current === opt.value;
          return (
            <button
              key={opt.value}
              type="button"
              role="radio"
              aria-checked={checked}
              onClick={() => onSelect(opt.value)}
              className="option-row flex items-center gap-3 px-3"
            >
              <span
                className="shrink-0 w-8 h-8 rounded-lg bg-primary-50 text-primary-700 text-[11px] font-semibold tracking-wide flex items-center justify-center"
                aria-hidden
              >
                {opt.badge}
              </span>
              <span className="flex-1 text-sm font-medium text-slate">{opt.label}</span>
              <span className="option-dot" />
            </button>
          );
        })}
      </div>

      <p className="text-[11px] text-slate-muted mt-3">
        {de
          ? 'Die Seite lädt kurz neu, wenn du die Sprache änderst.'
          : 'The page will briefly reload when you change the language.'}
      </p>
    </section>
  );
}

// ════════════════════════════════════════════════════════════════════
// LegalSection — moved from Settings on 2026-05-31. Entry points to
// Imprint + Privacy Policy. Mandatory per Swiss UWG Art. 3 lit. s +
// DSGVO Art. 13 (easily reachable from the app).
// ════════════════════════════════════════════════════════════════════

function LegalSection() {
  const de = isGerman();

  return (
    <section className="card p-5">
      <h2 className="font-display font-semibold text-slate">
        {de ? 'Rechtliches' : 'Legal'}
      </h2>
      <div className="mt-3 -mx-2">
        <Link
          to="/imprint"
          className="flex items-center justify-between px-2 py-3 rounded-lg hover:bg-primary-50/30 transition-colors"
        >
          <span className="text-sm text-slate">{de ? 'Impressum' : 'Imprint'}</span>
          <Icon.ArrowRight size={16} className="text-slate-muted" />
        </Link>
        <Link
          to="/privacy"
          className="flex items-center justify-between px-2 py-3 rounded-lg hover:bg-primary-50/30 transition-colors"
        >
          <span className="text-sm text-slate">
            {de ? 'Datenschutzerklärung' : 'Privacy policy'}
          </span>
          <Icon.ArrowRight size={16} className="text-slate-muted" />
        </Link>
      </div>
    </section>
  );
}
